/* Sticky header custom element */
class StickyHeader extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.header = document.querySelector('.shopify-section-header');
		this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
		this.headerBounds = {};
		this.currentScrollTop = 0;
		this.preventReveal = false;

		if (this.headerIsAlwaysSticky) {
			this.header.classList.add('shopify-section-header-sticky');
			this.header.classList.add('always-fixed');
		}

		this.onScrollHandler = this.onScroll.bind(this);
		this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
		this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
		window.addEventListener('scroll', this.onScrollHandler, false);

		this.createObserver();
	}

	disconnectedCallback() {
		this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
		window.removeEventListener('scroll', this.onScrollHandler);
	}

	createObserver() {
		const observer = new IntersectionObserver((entries, obs) => {
			this.headerBounds = entries[0].intersectionRect;
			obs.disconnect();
		});
		observer.observe(this.header);
	}

	onScroll() {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
			if (this.preventHide) return;
			requestAnimationFrame(this.hide.bind(this));
		} else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
			if (!this.preventReveal) {
				requestAnimationFrame(this.reveal.bind(this));
			} else {
				window.clearTimeout(this.isScrolling);
				this.isScrolling = setTimeout(() => {
					this.preventReveal = false;
				}, 66);
				requestAnimationFrame(this.hide.bind(this));
			}
		} else if (scrollTop <= this.headerBounds.top) {
			requestAnimationFrame(this.reset.bind(this));
		}

		this.currentScrollTop = scrollTop;
	}

	hide() {
		if (this.headerIsAlwaysSticky) return;
		this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
		this.closeMenuDisclosure();
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return;
		this.header.classList.add('shopify-section-header-sticky', 'animate');
		this.header.classList.remove('shopify-section-header-hidden');
	}

	reset() {
		if (this.headerIsAlwaysSticky) return;
		this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
	}

	closeMenuDisclosure() {
		this.disclosures = this.disclosures || this.header.querySelectorAll('details-disclosure');
		this.disclosures.forEach((disclosure) => disclosure.close());
	}
}

customElements.define('sticky-header', StickyHeader);

/* Mega menu backdrop — darkens page behind open mega menu */
(function () {
	const MIN_WIDTH = 1100;

	const getBackdrop = () => {
		let backdrop = document.querySelector('.header-mega-menu-backdrop');
		if (!backdrop) {
			backdrop = document.createElement('div');
			backdrop.className = 'header-mega-menu-backdrop';
			backdrop.setAttribute('aria-hidden', 'true');
			const host = document.querySelector('.header-wrapper') || document.body;
			host.appendChild(backdrop);
		}
		return backdrop;
	};

	const bindBackdrop = () => {
		if (!document.querySelector('[data-mega-backdrop="true"]')) return;
		const backdrop = getBackdrop();
		document.querySelectorAll('.header .list-menu-has-child').forEach((el) => {
			if (el.dataset.backdropBound) return;
			el.dataset.backdropBound = '1';

			const show = () => {
				if (window.innerWidth >= MIN_WIDTH) backdrop.classList.add('is-active');
			};
			const hide = () => backdrop.classList.remove('is-active');

			el.addEventListener('mouseenter', show);
			el.addEventListener('mouseleave', hide);
			el.addEventListener('focusin', show);
			el.addEventListener('focusout', (e) => {
				if (!el.contains(e.relatedTarget)) hide();
			});
		});
	};

	bindBackdrop();
	document.addEventListener('shopify:section:load', bindBackdrop);
})();

/* Mega menu preview image swap on collection-link hover */
(function () {
	const bindPreview = () => {
		document.querySelectorAll('.header-mega-menu').forEach((menu) => {
			if (menu.dataset.previewBound) return;
			menu.dataset.previewBound = '1';

			const previews = menu.querySelectorAll('.header-mega-menu__preview-image');
			menu.querySelectorAll('.header-mega-menu__collection-link').forEach((link) => {
				link.addEventListener('mouseenter', () => {
					const id = link.dataset.previewTarget;
					previews.forEach((p) => {
						p.classList.toggle('is-active', p.dataset.previewId === id);
					});
				});
			});
		});
	};

	bindPreview();
	document.addEventListener('shopify:section:load', bindPreview);
})();

/* Expose dynamic header height as CSS var */
(function () {
	const calcHeader = () => {
		const header = document.querySelector('.shopify-section-header');
		if (!header) return;

		const observer = new ResizeObserver(() => {
			const height = header.getBoundingClientRect().height;
			document.documentElement.style.setProperty('--header-dynamic-height', `${height}px`);
		});
		observer.observe(header);
	};

	calcHeader();
	document.addEventListener('shopify:section:load', calcHeader);
	document.addEventListener('shopify:section:unload', calcHeader);
	document.addEventListener('shopify:section:reorder', calcHeader);
})();

/* Expose sticky announcement-bar height so the header can stack below it */
(function () {
	const setRoot = document.documentElement.style;
	const calcAnnouncement = () => {
		const bar = document.querySelector('.announcement-bar[data-sticky="true"]');
		const section = bar && bar.closest('.section-announcement');
		if (!section) {
			setRoot.setProperty('--announcement-bar-height', '0px');
			return;
		}
		const observer = new ResizeObserver(() => {
			setRoot.setProperty('--announcement-bar-height', `${section.getBoundingClientRect().height}px`);
		});
		observer.observe(section);
	};

	calcAnnouncement();
	document.addEventListener('shopify:section:load', calcAnnouncement);
	document.addEventListener('shopify:section:unload', calcAnnouncement);
	document.addEventListener('shopify:section:reorder', calcAnnouncement);
})();
